import { MISSION_REWARD_LAST, MISSION_TIMERS } from '../balance.js';
import { levelTable } from '../data/index.js';
import { GameError } from '../errors.js';
import type { GameEvent } from '../events.js';
import { premiumOf } from '../premium.js';
import { randInt, type Rng } from '../rng.js';
import type { BotUser } from '../state.js';
import { applyLevelUps } from './levels.js';
import { checkSuccessGroups } from './success.js';

const HOUR = 60 * 60 * 1000;
/** `idle.delta` / `idle.delta_week` lengths kept by the bot. */
const DELTA_KEEP = 720;
const DELTA_WEEK_KEEP = 28;

export function isWeekend(now: number): boolean {
  const day = new Date(now).getDay();
  return day === 0 || day === 6;
}

export function missionDurationMs(kind: number): number {
  return (MISSION_TIMERS[kind]?.time ?? 0) * HOUR;
}

/** Epoch ms when the current mission ends, or null when none was ever sent. */
export function missionEndsAt(save: BotUser): number | null {
  if (save.idle.expe === 0) return null;
  return save.idle.date + missionDurationMs(save.idle.type);
}

export type MissionStatus = 'idle' | 'running' | 'done';

/** The bot's gate: the team is back once `now − idle.date > duration`. */
export function missionStatus(save: BotUser, now: number): MissionStatus {
  const end = missionEndsAt(save);
  if (end === null) return 'idle';
  return now > end ? 'done' : 'running';
}

export interface MissionRewards {
  money: number;
  lootbox: number;
  xp: number;
  weekend: boolean;
}

function premiumFactors(save: BotUser, now: number): { fakecoin: number; lootcoin: number } {
  const premium = premiumOf(save, now);
  if (premium.premium <= 0) return { fakecoin: 1, lootcoin: 1 };
  return { fakecoin: 1 + premium.mission.fakecoin / 100, lootcoin: 1 + premium.mission.lootcoin / 100 };
}

function moneyBase(level: number, kind: number, premFakecoin: number): number {
  const timer = MISSION_TIMERS[kind];
  if (!timer) return 0;
  const r = 1.1 - kind / 50;
  const ratio = 1 + level / 10;
  return ((100 * ratio * (1 + MISSION_REWARD_LAST.money / 100) * premFakecoin) / 6) * timer.time * r;
}

function levelEntryFor(level: number) {
  return levelTable[level >= levelTable.length ? levelTable.length - 1 : level];
}

/** `rpg/idle.ts#estimateExpeditionRewards` (display only, no RNG); lootcoins: expected value (parity P-8). */
export function estimateMission(save: BotUser, kind: number, now: number): MissionRewards {
  const timer = MISSION_TIMERS[kind];
  const weekend = isWeekend(now);
  if (!timer) return { money: 0, lootbox: 0, xp: 0, weekend };
  const prem = premiumFactors(save, now);
  const r = 1.1 - kind / 50;
  const money = Math.floor(moneyBase(save.level.level, kind, prem.fakecoin));
  const entry = levelEntryFor(save.level.level);
  let xp = 0;
  if (entry && entry.fight !== 0) {
    xp = Math.floor((entry.xp / entry.fight / 6) * timer.time * r);
    if (weekend) xp *= 2;
  }
  // Expected lootcoins of the roll loop: p/(100-p) with p = time·ratio (geometric).
  const p = Math.min(99, timer.time * timer.ratio);
  return { money, lootbox: Math.round((p / (100 - p)) * prem.lootcoin * 100) / 100, xp, weekend };
}

/** `rpg/idle.ts#lootExpedition` — roll order: lootbox loop, xp, money (parity K-9). */
export function rollMission(save: Pick<BotUser, 'level' | 'idle' | 'premium'>, now: number, rng: Rng): MissionRewards {
  const kind = save.idle.type;
  const level = save.level.level;
  const timer = MISSION_TIMERS[kind];
  const weekend = isWeekend(now);
  if (!timer) return { money: 0, lootbox: 0, xp: 0, weekend };
  const r = 1.1 - kind / 50;

  let lootboxCount = 0;
  while (randInt(rng, 0, 100) < timer.time * timer.ratio) lootboxCount++;

  const prem = premiumFactors(save as BotUser, now);
  const calcMoney = moneyBase(level, kind, prem.fakecoin);
  const moneyMin = Math.floor(calcMoney * 0.9);
  const moneyMax = Math.floor(calcMoney * 1.1);
  const lootbox = Math.floor(lootboxCount * prem.lootcoin);

  const entry = levelEntryFor(level);
  if (!entry || entry.fight === 0) {
    return { money: Math.floor(randInt(rng, moneyMin, moneyMax)), lootbox, xp: 0, weekend };
  }
  const xpBase = entry.xp / entry.fight / 6;
  let xp = Math.floor(randInt(rng, Math.floor(xpBase * 0.75 * timer.time), Math.floor(xpBase * 1.25 * timer.time)) * r);
  if (weekend) xp *= 2;
  const money = Math.floor(randInt(rng, moneyMin, moneyMax));
  return { money, lootbox, xp, weekend };
}

function trackDelta(save: BotUser, delta: number): void {
  const idle = save.idle;
  if (!idle.delta) {
    idle.delta = [];
    idle.delta_week = [];
    idle.average = 0;
    idle.average_week = 0;
  }
  if (idle.delta_week.length >= DELTA_WEEK_KEEP) idle.delta_week = idle.delta_week.slice(1);
  if (idle.delta.length >= DELTA_KEEP) idle.delta = idle.delta.slice(1);
  idle.delta.push(delta);
  idle.delta_week.push(delta);
  idle.average = idle.delta.reduce((a, d) => a + d, 0) / idle.delta.length;
  idle.average_week = idle.delta_week.reduce((a, d) => a + d, 0) / idle.delta_week.length;
}

/**
 * `fillExpeditionStart`: once the team is back, the finished mission is
 * collected and the team leaves again with the chosen duration. Bot quirk
 * (parity K-14): the very first mission (`idle.expe = 0`) keeps the stored
 * `idle.type`, whatever duration was chosen.
 */
export function startMission(save: BotUser, kind: number, now: number, rng: Rng, events: GameEvent[]): void {
  if (!MISSION_TIMERS[kind]) throw new GameError('unknownMission');
  if (save.summoned.team.equiped.length === 0) throw new GameError('needTeam');
  const delay = missionDurationMs(save.idle.type);
  const returnTime = now - save.idle.date;
  if (!(returnTime > delay || save.idle.date === 0)) {
    throw new GameError('missionInProgress', { endsAt: save.idle.date + delay });
  }

  if (save.idle.expe !== 0) {
    const rewards = rollMission(save, now, rng);
    save.money += rewards.money;
    save.stats.money.total += rewards.money;
    save.lootbox += rewards.lootbox;
    save.level.xp += rewards.xp;
    save.stats.mission.fake += rewards.money;
    save.stats.mission.loot += rewards.lootbox;
    save.stats.mission.xp += rewards.xp;
    trackDelta(save, Math.floor((returnTime - delay) / 1000));
    events.push({ type: 'missionRewards', ...rewards });
    applyLevelUps(save, events);
    checkSuccessGroups(save, ['level'], now, events);
    save.idle.expe += 1;
    save.stats.mission.count += 1;
    save.idle.date = now;
    save.idle.type = kind;
    checkSuccessGroups(save, ['missions'], now, events);
  } else {
    save.idle.date = now;
    save.idle.expe = 1;
  }
  events.push({ type: 'missionStarted', kind: save.idle.type, endsAt: now + missionDurationMs(save.idle.type) });
  // The bot checks intro achievements on a copy whose mission count is one ahead of what it stores.
  save.stats.mission.count += 1;
  checkSuccessGroups(save, ['intro'], now, events);
  save.stats.mission.count -= 1;
}
